/* global globalThis */
import { bytesToBase64, randomUuid } from './messengerV2Platform.js';
import { createMessengerV2ImageEditDocument } from './imageEditDocument.js';
import { createMessengerV2AttachmentError } from './attachmentErrors.js';

const ATTACHMENT_ENCRYPTION_ALGORITHM = 'AES-GCM';
const ATTACHMENT_ENCRYPTION_IV_BYTES = 12;

const toStringOrNull = (value) => {
  if (value == null) return null;
  const normalized = String(value).trim();
  return normalized || null;
};

const ensureCrypto = () => {
  const cryptoApi =
    (typeof globalThis !== 'undefined' && globalThis.crypto) ||
    (typeof window !== 'undefined' ? window.crypto : null);
  if (!cryptoApi?.subtle || typeof cryptoApi.getRandomValues !== 'function') {
    throw new Error('WebCrypto unavailable');
  }
  return cryptoApi;
};

const isBrowserFile = (value) =>
  typeof File !== 'undefined' && value instanceof File ? true : Boolean(value?.arrayBuffer);

const readFileArrayBuffer = async (file) => {
  if (!file) {
    throw new Error('Attachment file is required');
  }

  if (typeof file.arrayBuffer === 'function') {
    return file.arrayBuffer();
  }

  if (typeof FileReader !== 'undefined') {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error('Could not read attachment file'));
      reader.onload = () => resolve(reader.result);
      reader.readAsArrayBuffer(file);
    });
  }

  throw new Error('Attachment file reading is unavailable');
};

const resolveAttachmentKind = (file = null) => {
  const mimeType = toStringOrNull(file?.type ?? null)?.toLowerCase() || '';
  const name = toStringOrNull(file?.name ?? null)?.toLowerCase() || '';

  if (mimeType.startsWith('image/')) return 'image';
  if (mimeType.startsWith('video/')) return 'video';
  if (mimeType.startsWith('audio/')) return 'audio';
  if (
    mimeType === 'application/pdf' ||
    mimeType.includes('document') ||
    mimeType.includes('msword') ||
    mimeType.includes('officedocument') ||
    mimeType.startsWith('text/')
  ) {
    return 'document';
  }

  if (
    name.endsWith('.webp') ||
    name.endsWith('.png') ||
    name.endsWith('.jpg') ||
    name.endsWith('.jpeg') ||
    name.endsWith('.gif') ||
    name.endsWith('.avif') ||
    name.endsWith('.heic') ||
    name.endsWith('.heif')
  ) {
    return 'image';
  }
  if (
    name.endsWith('.mp4') ||
    name.endsWith('.m4v') ||
    name.endsWith('.webm') ||
    name.endsWith('.mov') ||
    name.endsWith('.ogg') ||
    name.endsWith('.ogv')
  ) {
    return 'video';
  }
  if (
    name.endsWith('.pdf') ||
    name.endsWith('.doc') ||
    name.endsWith('.docx') ||
    name.endsWith('.txt') ||
    name.endsWith('.rtf') ||
    name.endsWith('.xls') ||
    name.endsWith('.xlsx') ||
    name.endsWith('.csv') ||
    name.endsWith('.ppt') ||
    name.endsWith('.pptx')
  ) {
    return 'document';
  }

  return 'file';
};

const resolveAttachmentName = (file = null) => toStringOrNull(file?.name ?? null) || 'attachment';

const resolveAttachmentMimeType = (file = null) =>
  toStringOrNull(file?.type ?? null) || 'application/octet-stream';

const encryptAttachmentBytes = async (arrayBuffer) => {
  const cryptoApi = ensureCrypto();
  const key = await cryptoApi.subtle.generateKey(
    { name: ATTACHMENT_ENCRYPTION_ALGORITHM, length: 256 },
    true,
    ['encrypt', 'decrypt'],
  );
  const exportedKey = await cryptoApi.subtle.exportKey('raw', key);
  const iv = new Uint8Array(ATTACHMENT_ENCRYPTION_IV_BYTES);
  cryptoApi.getRandomValues(iv);
  const ciphertext = await cryptoApi.subtle.encrypt(
    { name: ATTACHMENT_ENCRYPTION_ALGORITHM, iv },
    key,
    arrayBuffer,
  );

  return {
    bytes: new Uint8Array(ciphertext),
    encryption: {
      algorithm: ATTACHMENT_ENCRYPTION_ALGORITHM,
      keyB64: bytesToBase64(new Uint8Array(exportedKey)),
      ivB64: bytesToBase64(iv),
    },
  };
};

const uploadEncryptedBytes = async ({
  signedUrl,
  bytes,
  signal = null,
  onProgress = null,
  onByteProgress = null,
}) => {
  if (typeof XMLHttpRequest !== 'undefined' && typeof onProgress === 'function') {
    await new Promise((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open('PUT', signedUrl, true);
      request.setRequestHeader('Content-Type', 'application/octet-stream');
      request.upload.onprogress = (event) => {
        if (!event.lengthComputable) return;
        const progress = Math.min(100, Math.round((event.loaded / event.total) * 100));
        onProgress(progress);
        onByteProgress?.({ loaded: event.loaded, total: event.total, progress });
      };
      request.onload = () => {
        if (request.status >= 200 && request.status < 300) {
          onProgress(100);
          onByteProgress?.({ loaded: bytes.byteLength, total: bytes.byteLength, progress: 100 });
          resolve();
        } else {
          const error = new Error(`Encrypted attachment upload failed (${request.status})`);
          error.status = request.status;
          reject(error);
        }
      };
      request.onerror = () => {
        const error = new Error('Encrypted attachment upload network error');
        error.code = 'ATTACHMENT_STORAGE_NETWORK';
        error.stage = 'storage_upload';
        reject(error);
      };
      request.onabort = () =>
        reject(
          Object.assign(new Error('Attachment upload aborted'), {
            name: 'AbortError',
          }),
        );
      if (signal) {
        if (signal.aborted) {
          request.abort();
          return;
        }
        signal.addEventListener('abort', () => request.abort(), { once: true });
      }
      request.send(bytes);
    });
    return;
  }

  const response = await fetch(signedUrl, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/octet-stream',
    },
    body: bytes,
    signal: signal || undefined,
  });

  if (!response.ok) {
    const error = new Error(`Encrypted attachment upload failed (${response.status})`);
    error.status = response.status;
    error.code = 'ATTACHMENT_STORAGE_HTTP';
    error.stage = 'storage_upload';
    throw error;
  }
  onProgress?.(100);
  onByteProgress?.({ loaded: bytes.byteLength, total: bytes.byteLength, progress: 100 });
};

const resolveSignedObjectUrl = (signedUrl = null) => {
  const normalized = toStringOrNull(signedUrl);
  if (!normalized) {
    return null;
  }

  try {
    const parsed = new URL(normalized);
    parsed.search = '';
    parsed.hash = '';
    return parsed.toString();
  } catch {
    return normalized.split('?')[0] || null;
  }
};

const createPreparedAttachmentRecord = ({ prepared, objectKey, signedUrl = null }) => ({
  id: objectKey,
  objectKey,
  object_key: objectKey,
  url: resolveSignedObjectUrl(signedUrl),
  fileUrl: resolveSignedObjectUrl(signedUrl),
  downloadUrl: resolveSignedObjectUrl(signedUrl),
  name: prepared.name || 'attachment',
  mimeType: prepared.mimeType || 'application/octet-stream',
  kind: prepared.kind || 'file',
  size: Math.max(0, Number(prepared.size) || 0),
  encryption: prepared.encryption,
});

export const createMessengerV2AttachmentDraft = (file = null) => {
  if (!isBrowserFile(file)) {
    return null;
  }

  const kind = resolveAttachmentKind(file);
  const supportsPreview =
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function' &&
    (kind === 'image' || kind === 'video');

  return {
    draftId: randomUuid(),
    file,
    name: resolveAttachmentName(file),
    mimeType: resolveAttachmentMimeType(file),
    size: Number.isFinite(Number(file?.size)) ? Math.max(0, Math.trunc(Number(file.size))) : 0,
    kind,
    caption: '',
    previewUrl: supportsPreview ? URL.createObjectURL(file) : null,
    editDocument: kind === 'image' ? createMessengerV2ImageEditDocument() : null,
  };
};

export const revokeMessengerV2AttachmentDraftPreview = (draft = null) => {
  const previewUrl = toStringOrNull(draft?.previewUrl ?? null);
  if (previewUrl && typeof URL !== 'undefined' && typeof URL.revokeObjectURL === 'function') {
    try {
      URL.revokeObjectURL(previewUrl);
    } catch {}
  }
};

export const prepareMessengerV2AttachmentUpload = async ({
  file,
  filename = null,
  contentType = null,
} = {}) => {
  if (!file) {
    throw new Error('Attachment file is required');
  }

  const name = toStringOrNull(filename ?? file?.name ?? null) || 'attachment';
  const mimeType = toStringOrNull(contentType ?? file?.type ?? null) || 'application/octet-stream';
  const kind = resolveAttachmentKind(file);
  const arrayBuffer = await readFileArrayBuffer(file);
  const encrypted = await encryptAttachmentBytes(arrayBuffer);

  return {
    bytes: encrypted.bytes,
    encryption: encrypted.encryption,
    name,
    mimeType,
    kind,
    size: Number.isFinite(Number(file?.size)) ? Math.max(0, Math.trunc(Number(file.size))) : 0,
  };
};

export const uploadPreparedMessengerV2Attachment = async ({
  prepared,
  createSignedUrl,
  signal = null,
  onProgress = null,
  onSigned = null,
} = {}) => {
  if (!prepared?.bytes || !prepared?.encryption) {
    throw new Error('Prepared encrypted attachment is required');
  }
  if (typeof createSignedUrl !== 'function') {
    throw new Error('createSignedUrl is required');
  }

  const { data } = await createSignedUrl({
    variables: {
      filename: prepared.name || 'attachment',
      contentType: 'application/octet-stream',
    },
  });

  const signedUrl = data?.createSignedUrl?.signedUrl;
  const objectKey = data?.createSignedUrl?.objectKey;
  if (!signedUrl || !objectKey) {
    throw new Error('Signed URL generation failed');
  }

  const attachment = createPreparedAttachmentRecord({ prepared, objectKey, signedUrl });
  await onSigned?.({ signedUrl, objectKey, attachment, prepared });
  await uploadEncryptedBytes({
    signedUrl,
    bytes: prepared.bytes,
    signal,
    onProgress,
  });

  return attachment;
};

const normalizeMultipartSession = (payload = null, existingSession = null) => {
  const uploadId = toStringOrNull(
    payload?.upload_id ?? payload?.uploadId ?? existingSession?.uploadId,
  );
  const objectKey = toStringOrNull(
    payload?.object_key ?? payload?.objectKey ?? existingSession?.objectKey,
  );
  const partSize = Math.max(
    1,
    Math.trunc(Number(payload?.part_size ?? payload?.partSize ?? existingSession?.partSize) || 0),
  );
  const incomingParts = Array.isArray(payload?.parts)
    ? payload.parts
    : existingSession?.parts || [];
  const previousParts = new Map(
    (Array.isArray(existingSession?.parts) ? existingSession.parts : []).map((part) => [
      Number(part?.partNumber),
      part,
    ]),
  );
  const parts = incomingParts
    .map((part) => {
      const partNumber = Math.trunc(Number(part?.part_number ?? part?.partNumber) || 0);
      if (partNumber < 1) return null;
      const previous = previousParts.get(partNumber) || null;
      return {
        ...previous,
        partNumber,
        signedUrl:
          toStringOrNull(part?.signed_url ?? part?.signedUrl ?? null) ||
          previous?.signedUrl ||
          null,
        uploaded: previous?.uploaded === true || part?.uploaded === true,
      };
    })
    .filter(Boolean)
    .sort((left, right) => left.partNumber - right.partNumber);

  if (!uploadId || !objectKey || !partSize || parts.length === 0) {
    throw new Error('Invalid multipart attachment upload session');
  }
  return {
    uploadId,
    objectKey,
    partSize,
    parts,
    completed: payload?.completed === true || existingSession?.completed === true,
  };
};

export const uploadResumablePreparedMessengerV2Attachment = async ({
  prepared,
  session: existingSession = null,
  createMultipartUpload,
  refreshMultipartUpload,
  completeMultipartUpload,
  signal = null,
  onProgress = null,
  onSession = null,
} = {}) => {
  if (!prepared?.bytes || !prepared?.encryption) {
    throw new Error('Prepared encrypted attachment is required');
  }
  if (
    typeof createMultipartUpload !== 'function' ||
    typeof refreshMultipartUpload !== 'function' ||
    typeof completeMultipartUpload !== 'function'
  ) {
    throw new Error('Multipart attachment upload API is unavailable');
  }

  let session = existingSession;
  if (!session?.uploadId || !session?.objectKey || !Array.isArray(session?.parts)) {
    let payload;
    try {
      payload = await createMultipartUpload({
        filename: prepared.name || 'attachment',
        contentType: 'application/octet-stream',
        byteSize: prepared.bytes.byteLength,
      });
    } catch (error) {
      throw createMessengerV2AttachmentError('multipart_start', error);
    }
    session = normalizeMultipartSession(payload);
    await onSession?.(session);
  } else {
    session = normalizeMultipartSession(null, session);
  }

  if (session.completed) {
    onProgress?.(100);
    return {
      attachment: createPreparedAttachmentRecord({
        prepared,
        objectKey: session.objectKey,
        signedUrl: null,
      }),
      session,
    };
  }

  const totalBytes = prepared.bytes.byteLength;
  const calculateCompletedBytes = () =>
    session.parts.reduce((total, part) => {
      if (!part.uploaded) return total;
      const start = (part.partNumber - 1) * session.partSize;
      return total + Math.max(0, Math.min(session.partSize, totalBytes - start));
    }, 0);

  for (let index = 0; index < session.parts.length; index += 1) {
    let part = session.parts[index];
    if (part.uploaded) continue;
    if (signal?.aborted) {
      throw Object.assign(new Error('Attachment upload paused'), { name: 'AbortError' });
    }

    const start = (part.partNumber - 1) * session.partSize;
    const end = Math.min(totalBytes, start + session.partSize);
    const chunk = prepared.bytes.subarray(start, end);
    const uploadPart = async () =>
      uploadEncryptedBytes({
        signedUrl: part.signedUrl,
        bytes: chunk,
        signal,
        onProgress: () => {},
        onByteProgress: ({ loaded }) => {
          const progress = Math.min(
            100,
            Math.round(((calculateCompletedBytes() + loaded) / totalBytes) * 100),
          );
          onProgress?.(progress);
        },
      });

    try {
      // eslint-disable-next-line no-await-in-loop
      await uploadPart();
    } catch (error) {
      if (error?.name === 'AbortError' || ![401, 403].includes(Number(error?.status))) throw error;
      // Signed part URLs can expire while a job is paused. Refresh only the pending part.
      // eslint-disable-next-line no-await-in-loop
      const refreshed = await refreshMultipartUpload({
        objectKey: session.objectKey,
        uploadId: session.uploadId,
        partNumbers: [part.partNumber],
      });
      const refreshedSession = normalizeMultipartSession(
        {
          ...refreshed,
          upload_id: session.uploadId,
          object_key: session.objectKey,
          part_size: session.partSize,
        },
        session,
      );
      const refreshedPart = refreshedSession.parts.find(
        (candidate) => candidate.partNumber === part.partNumber,
      );
      if (!refreshedPart?.signedUrl) throw error;
      session = {
        ...session,
        parts: session.parts.map((candidate) =>
          candidate.partNumber === part.partNumber
            ? { ...candidate, signedUrl: refreshedPart.signedUrl }
            : candidate,
        ),
      };
      part = session.parts[index];
      // eslint-disable-next-line no-await-in-loop
      await onSession?.(session);
      // eslint-disable-next-line no-await-in-loop
      await uploadPart();
    }

    session = {
      ...session,
      parts: session.parts.map((candidate) =>
        candidate.partNumber === part.partNumber
          ? { ...candidate, uploaded: true, signedUrl: null }
          : candidate,
      ),
    };
    // Persist each completed part before starting the next one. A pause or process exit
    // therefore resumes at the first unfinished part instead of restarting the file.
    // eslint-disable-next-line no-await-in-loop
    await onSession?.(session);
    onProgress?.(Math.min(100, Math.round((calculateCompletedBytes() / totalBytes) * 100)));
  }

  try {
    await completeMultipartUpload({
      objectKey: session.objectKey,
      uploadId: session.uploadId,
      expectedParts: session.parts.length,
    });
  } catch (error) {
    throw createMessengerV2AttachmentError('multipart_complete', error);
  }
  session = { ...session, completed: true };
  await onSession?.(session);
  onProgress?.(100);
  const firstSignedUrl =
    existingSession?.parts?.find((part) => part?.signedUrl)?.signedUrl ||
    session.parts.find((part) => part?.signedUrl)?.signedUrl ||
    null;
  return {
    attachment: createPreparedAttachmentRecord({
      prepared,
      objectKey: session.objectKey,
      signedUrl: firstSignedUrl,
    }),
    session,
  };
};

export const encryptAndUploadMessengerV2Attachment = async ({
  file,
  createSignedUrl,
  filename = null,
  contentType = null,
  signal = null,
  onProgress = null,
} = {}) => {
  const prepared = await prepareMessengerV2AttachmentUpload({
    file,
    filename,
    contentType,
  });
  return uploadPreparedMessengerV2Attachment({
    prepared,
    createSignedUrl,
    signal,
    onProgress,
  });
};

export const encryptAndUploadMessengerV2Attachments = async ({
  files = [],
  createSignedUrl,
  signal = null,
} = {}) => {
  const normalizedFiles = Array.isArray(files) ? files : [];
  const results = [];

  for (const file of normalizedFiles) {
    // Serializing uploads keeps the first pass simple and predictable.
    // Upload progress can be layered in later without changing the envelope format.
    // eslint-disable-next-line no-await-in-loop
    const attachment = await encryptAndUploadMessengerV2Attachment({
      file,
      createSignedUrl,
      signal,
    });
    results.push(attachment);
  }

  return results;
};

const messengerV2AttachmentTransport = {
  createMessengerV2AttachmentDraft,
  encryptAndUploadMessengerV2Attachment,
  encryptAndUploadMessengerV2Attachments,
  prepareMessengerV2AttachmentUpload,
  revokeMessengerV2AttachmentDraftPreview,
  uploadPreparedMessengerV2Attachment,
  uploadResumablePreparedMessengerV2Attachment,
};

export default messengerV2AttachmentTransport;
